import { Subscription } from 'referral-types';

import PaperAirplaneIcon from '@heroicons/react/20/solid/PaperAirplaneIcon';
import { Card } from '../../_misc/layouts/Card';
import { usePurchase } from '../../../context/purchase/usePurchase';
import classNames from 'classnames';
import { Button } from '../../_misc/buttons/Button';

import { ButtonType } from '../../_misc/buttons/ButtonType';
import { useNavigate } from 'react-router-dom';
import { useDotsLabel } from '../../../hooks/useDotsLabel';
import { useAuth } from '../../../context/auth/useAuth';
import { valToDateString } from '../../../utils';
import { ThemeBackground, ThemeText } from '../../../context/theme/ThemeConstantsUpdated';

const { borderBackground } = ThemeBackground;
const { mainText, secondaryText } = ThemeText;

export const SubscriptionInfo = (props: Props) => {
  const { subscription, onContinue } = props;
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { completedSubscription, isRefreshingToken } = usePurchase();
  const statusSuffix = useDotsLabel();

  return (
    <>
      <Card
        header={
          <div className={classNames('border-b pb-3 text-left', borderBackground)}>
            <h3 className={classNames(secondaryText, '')}>Thank you!</h3>
            <h1 className={classNames(mainText, 'text-4xl font-semibold')}>Order Complete</h1>
          </div>
        }
        footer={
          <div className="flex items-center justify-between pb-5 pt-4">
            {isRefreshingToken ? (
              <span className={classNames(secondaryText, 'font-medium')}>Refreshing Profile{statusSuffix}</span>
            ) : (
              <div />
            )}
            <div className="flex items-center justify-end gap-5">
              <Button
                onClick={() => {
                  onContinue(completedSubscription);
                  if (currentUser?.id != null) {
                    setTimeout(() => navigate(`/user/${currentUser.id}?tab=subscription`), 800);
                  }
                }}
                buttonType={ButtonType.Link}
              >
                Order History
              </Button>
              <Button
                Icon={PaperAirplaneIcon}
                onClick={() => onContinue(completedSubscription)}
                disabled={isRefreshingToken}
                options={{ tooltip: isRefreshingToken ? 'Refreshing Profile' : undefined }}
              >
                Continue
              </Button>
            </div>
          </div>
        }
      >
        <>
          <div className={classNames('space-y-0.5 px-0.5 py-2 text-sm')}>
            <div className="flex justify-between">
              <span className={classNames(mainText, 'font-semibold')}>Subscription</span>
              <span className={classNames(secondaryText)}>{subscription.productTitle}</span>
            </div>

            <div className="flex justify-between">
              <span className={classNames(mainText, 'font-semibold')}>Order Date</span>
              <span className={classNames(secondaryText)}>{valToDateString(subscription.createdDateTime)}</span>
            </div>
          </div>

          <div className={classNames(borderBackground, 'space-y-0.5 border-t py-2 text-sm')}>
            <div className="flex justify-between px-0.5">
              <span className={classNames(mainText, 'font-semibold')}>Subtotal</span>
              <span className={classNames(secondaryText)}>${subscription.productPrice}</span>
            </div>

            <div className="flex justify-between px-0.5">
              <span className={classNames(mainText, 'font-semibold')}>Renewal Date</span>
              <span className={classNames(secondaryText)}>{valToDateString(subscription.endDate)}</span>
            </div>
          </div>

          <div
            className={classNames(
              borderBackground,
              'flex items-center justify-between border-t px-0.5 pb-4 pt-3 text-xl font-semibold'
            )}
          >
            <span className={classNames(mainText)}>Status</span>
            <span className={secondaryText}>{subscription.status.toUpperCase()}</span>
          </div>
        </>
      </Card>
    </>
  );
};

type Props = {
  subscription: Subscription;
  onContinue: (subscription?: Subscription) => void;
};
